import {
    Box,
    Button, Chip,
    Grid,
    Link,
    Stack,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { LocalityType } from "../../enums/LocalityEnum";
import EventBus from "../../EventBus";
import { CookLocalityMapping } from "../../interfaces/Cook/CookLocalityMapping";
import { CookService } from "../../services/CookServices";
import CkMuiDialog from "../Common/CkMuiDialog/CkMuiDialog";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

const cookService = new CookService();

interface Props {
    cookId: number;
}

function CookLocalityTab({ cookId }: Props) {
    const [localityData, setLocalityData] = useState<Array<CookLocalityMapping>>();
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [selectedSlc] = useState<any>();
    const [selectedSlcType, setSelectedSlcType] = useState<LocalityType[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [subData, setSubData] = useState<any>(null);
    const [localityId, setLocalityId] = useState<number>(NaN);

    const fetchDetails = useCallback(
        (limit: number, offset: number) => {
            cookService.getLocalityMapping(cookId, limit, offset).then((res) => {
                setSubData(res?.data);
                setLocalityData(res?.data?.data);
            });
        },
        [cookId]
    );

    const handleDeleteLocality = () => {
        cookService
            .deleteLocality(cookId, localityId)
            .then(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Locality Deleted Successfully",
                    type: CustomSnackbarTypes.SUCCESS,
                });
                fetchDetails(rowsPerPage, page * rowsPerPage);
            })
            .catch(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Error Deleting Locality",
                    type: CustomSnackbarTypes.ERROR,
                });
            })
            .finally(() => {
                setDeleteConfirmDialogOpen(false);
            });
    };

    const handleDeleteLocalityDialog = (id: number) => {
        setDeleteConfirmDialogOpen(true);
        setLocalityId(id);
    };

    const handleUpdateLocality = () => {
        const params = {
            localityId: selectedSlc?.localityId,
            areaId: selectedSlc?.localityDetails?.areaId,
            cityId: selectedSlc?.localityDetails?.cityId,
            preference: selectedSlc?.preference,
            localityType: selectedSlcType,
        };
        cookService.addLocalityMapping(cookId, params).then(() => {
            setUpdateDialogOpen(false);
            fetchDetails(rowsPerPage, page * rowsPerPage);
        });
    };

    useEffect(() => {
        fetchDetails(rowsPerPage, page * rowsPerPage);
    }, [fetchDetails, rowsPerPage, page]);

    const columns: CommonListColumn[] = [
        { label: 'ID', field: 'id' },
        {
            label: 'Locality Id',
            render: (row) => (
                <Link href={`/#/Localities/${row?.localityId}/show`}>{row?.localityId}</Link>
            ),
        },
        { label: 'Locality Name', render: (row) => row?.localityDetails?.name ?? '-' },
        { label: 'Locality Type', render: (row) => row?.slcTypeList?.join(', ') ?? '-' },
        { label: 'Area Name', render: (row) => row?.localityDetails?.areaName ?? '-' },
        {
            label: 'Delete Locality',
            stopRowClick: true,
            render: (row) => (
                <Button
                    onClick={() => handleDeleteLocalityDialog(row.localityId)}
                    color="error"
                    variant="outlined"
                    size="small"
                >
                    Delete
                </Button>
            ),
        },
    ];

    return (
        <Box>
            <CkMuiDialog
                isDialogOpen={deleteConfirmDialogOpen}
                setDialogOpen={setDeleteConfirmDialogOpen}
                onDialogClose={() => {}}
                title=""
                primaryLabel="Yes"
                primaryAction={handleDeleteLocality}
                secondaryLabel="No"
                secondaryAction={() => setDeleteConfirmDialogOpen(false)}
                isLoading={false}
            >
                <Grid container>
                    <Grid item xs={12}>Are you sure you want to delete the locality</Grid>
                </Grid>
            </CkMuiDialog>
            <CkMuiDialog
                isDialogOpen={updateDialogOpen}
                setDialogOpen={setUpdateDialogOpen}
                onDialogClose={() => setUpdateDialogOpen(false)}
                title="Update Locality Type"
                primaryLabel="Confirm"
                primaryAction={handleUpdateLocality}
                secondaryLabel="Cancel"
                secondaryAction={() => setUpdateDialogOpen(false)}
                isLoading={false}
            >
                <Box pr={2} py={2}>
                    <Stack direction="row" spacing={1}>
                        {Object.values(LocalityType).map((each: LocalityType) => {
                            const isExists = selectedSlcType?.includes(each);
                            return (
                                <Chip
                                    key={each}
                                    label={each}
                                    variant={isExists ? 'filled' : 'outlined'}
                                    onClick={() => {
                                        if (isExists) {
                                            setSelectedSlcType((prev) => prev.filter((e) => e !== each));
                                        } else {
                                            setSelectedSlcType((prev) => [...prev, each]);
                                        }
                                    }}
                                    onDelete={
                                        isExists
                                            ? () => setSelectedSlcType((prev) => prev.filter((e) => e !== each))
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </CkMuiDialog>
            <CommonListTable
                columns={columns}
                data={localityData ?? []}
                loading={false}
                page={page}
                rowsPerPage={rowsPerPage}
                total={subData?.count ?? 0}
                onPageChange={setPage}
                onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setPage(0); }}
                emptyText="No Locality Found"
            />
        </Box>
    );
}

export default CookLocalityTab;
